import React from 'react';
import { apiService } from './api';
import { STORAGE_KEYS } from './constants';
import {
  ENUM_STATUS,
  ENUM_KITCHEN_STATUS,
  ENUM_TRIP_STATUS,
  ENUM_KITHEN_TEAMS,
  ENUM_ORDER_PROVIDER
} from './enum';
import moment from 'moment';
import { ENUM_BANK_NAMES } from './enum';
import { ENUM_DATE_RANGE, ToastMessage } from '@cokitchen/cokitchen-components';
import { TripHistoryType } from 'models/trip-history.model';
import { OrderType } from 'models/order.model';
import { CoKitchenType } from 'models/cokitchen.model';
import { LiveTripOrderType } from 'models/live-trip.model';
import { UserType } from 'models/user.model';
import { ENUM_COKITCHEN_TEAM, ENUM_ROLE } from '@cokitchen/cokitchen-auth';
import { toast } from 'react-toastify';
import { getCurrentTeam } from './role-config';

export const getOrderStatus = (order: OrderType): ENUM_STATUS => {
  // TODO: Compute order state
  let status = ENUM_STATUS?.PENDING;
  const {
    kitchen_prepared,
    kitchen_dispatched,
    calculated_order,
    kitchen_accepted,
    shop_accepted,
    shop_prepared,
    kitchen_cancelled,
    cancelled,
    completed,
    paid,
    order_recalled
  } = order;
  if (kitchen_cancelled || cancelled) {
    status = ENUM_STATUS.CANCELLED;
  } else if (completed) {
    status = ENUM_STATUS.COMPLETED;
  } else if (!paid && !kitchen_accepted) {
    status = ENUM_STATUS.UNPAID;
  } else if (
    (kitchen_accepted || shop_accepted) &&
    (!kitchen_prepared || !shop_prepared) &&
    !kitchen_dispatched
  ) {
    status = ENUM_STATUS.PROCESSING;
  } else if (
    kitchen_prepared &&
    shop_prepared &&
    !kitchen_dispatched &&
    !calculated_order?.pickup &&
    !order_recalled
  ) {
    status = ENUM_STATUS.READY;
  } else if (order_recalled && !kitchen_dispatched) {
    return ENUM_STATUS.RECALLED;
  } else if (!calculated_order?.pickup && kitchen_dispatched) {
    status = ENUM_STATUS.ONGOING;
  } else if (paid && !kitchen_accepted && !shop_accepted) {
    status = ENUM_STATUS.PENDING;
  }
  return status;
};

export const getKitchenOrderStatus = (order: OrderType): ENUM_KITCHEN_STATUS | 'complete' => {
  // TODO: Compute order state
  const {
    kitchen_prepared,
    kitchen_accepted,
    shop_accepted,
    shop_prepared,
    kitchen_time_elapsed,
    paid,
    order_recalled
  } = order;

  if (!paid) {
    return ENUM_KITCHEN_STATUS.UNPAID;
  } else if (order_recalled) {
    return ENUM_KITCHEN_STATUS.RECALLED;
  } else if (kitchen_prepared || shop_prepared) {
    return ENUM_KITCHEN_STATUS.DISPATCH;
  } else if (kitchen_accepted || shop_accepted) {
    return ENUM_KITCHEN_STATUS.PROCESSING;
  } else if (kitchen_time_elapsed) {
    return ENUM_KITCHEN_STATUS.LATE;
  } else if (paid && !kitchen_accepted && !shop_accepted) {
    return ENUM_KITCHEN_STATUS.PENDING;
  }

  return ENUM_KITCHEN_STATUS.PENDING;
};

export const getTripStatus = (tripHistory: TripHistoryType): ENUM_TRIP_STATUS => {
  const { status } = tripHistory;
  if (status) {
    return ENUM_TRIP_STATUS?.COMPLETED;
  }
  return ENUM_TRIP_STATUS?.FAILED;
};
// utils.js

export const calculateReactivationTime = (time: string | null) => {
  if (!time) return 0;
  const currentTime = new Date().getTime() / 1000;
  const reactivationTime = time ? new Date(time).getTime() / 1000 : 0;
  const returnVal = Math.max(0, reactivationTime - currentTime);
  return Math.ceil(returnVal);
};

export const formattTime = (seconds: any) => {
  if (seconds <= 0) {
    return '00:00:00';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');
  const formattedSeconds = remainingSeconds.toString().padStart(2, '0');

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

const MONTHS_OF_YEAR = [
  'Janaury',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

export const initKitchenData = async () => {
  const url = 'internal/cokitchens';
  const res = await apiService.get(url, {
    baseUrl: process.env.REACT_APP_BRAND_MGT_API_URL,
    isInternal: true
  });
  const data = res.data.data;
  localStorage.setItem(STORAGE_KEYS.COKITCHEN_LIST, JSON.stringify(data));
  return data;
};
export const getKitchenById = async (kitchen_id: string): Promise<CoKitchenType> => {
  let data = [];
  const dataString = localStorage.getItem(STORAGE_KEYS.COKITCHEN_LIST);
  if (dataString) {
    data = JSON.parse(dataString);
  } else {
    data = await initKitchenData();
  }
  return data.find((kitchen: CoKitchenType) => kitchen.id === kitchen_id);
};

const isDateValid = (date: string): boolean => {
  const dateValue = new Date(date);
  return !isNaN(dateValue.getTime());
};

const getOrdinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'],
    v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const getDate = (date: string): number | null | string => {
  const dateValue = new Date(date);
  const value = dateValue.getDate();
  const ordinalValue = getOrdinal(value);
  return isDateValid(date) ? ordinalValue : null;
};

export const getMonth = (date: string): string | null => {
  const dateValue = new Date(date);
  return isDateValid(date) ? MONTHS_OF_YEAR[dateValue.getMonth() ?? 0] : null;
};

export const getShortMonth = (date: string): string | null => {
  const month = getMonth(date);
  return month ? month.slice(0, 3) : null;
};

export const getYear = (date: string): number | null => {
  const dateValue = new Date(date);
  return isDateValid(date) ? dateValue.getFullYear() : null;
};

export const getSimpleDateFormat = (date: string): string | null => {
  return `${getDate(date)} ${getShortMonth(date)} ${getYear(date)}`;
};

export const formatDate = (timeStr: string) => {
  const dateString = new Date(timeStr).toDateString();
  // remove day
  return dateString.split(' ').slice(1).join(' ');
};

export const formatTime = (timeStr: string) => {
  return Intl.DateTimeFormat('en-NG', {
    timeStyle: 'short',
    hourCycle: 'h12'
  }).format(new Date(timeStr));
};

export const formatPhoneNumber = (number: string) => {
  // add - after every three characters
  let formattedNumber = '';
  for (let index = 0; index < number.length - 1; index += 1) {
    formattedNumber += number[index];
    if ((index + 1) % 3 === 0) {
      formattedNumber += '-';
    }
  }
  return formattedNumber;
};

export const formatSurgeDate = (date: any) => {
  const dateSet = String(date);
  const dateArray = dateSet?.split('-');
  const startDate = new Date(dateArray?.slice(0, 3).join('-'));
  const endDate = dateArray.length > 3 ? new Date(dateArray?.slice(3).join('-')) : startDate;

  return { startDate, endDate };
};
export const dateDifference = (a: any, b: any) => {
  const c = moment(b);
  const d = moment(a);

  return c.diff(d, 'days'); // 86400000
};
export const getUserInitials = (user?: { first_name: string; last_name: string }) => {
  const [first, last] = getUserName(user, ' ').split(' ');
  if (!first) return '-';

  if (!last) return first[0];

  return `${first[0]} ${last[0]}`;
};
export const getUserName = (user?: { first_name: string; last_name: string }, fallback = '-') => {
  if (!user) return fallback;
  const { first_name = '', last_name = '' } = user;

  if (!first_name && !last_name) return '-';

  if (!first_name) return last_name;

  if (!last_name) return first_name;

  return `${first_name} ${last_name}`;
};
export const getNameFromOrder = (order: OrderType): string => {
  const { calculated_order, user } = order;

  const _user = calculated_order?.user ?? user;
  return getUserName(_user);
};

export const formatDateForApi = ({ startDate, endDate }: { startDate: Date; endDate: Date }) => {
  return { start_date: startDate.toISOString(), end_date: endDate.toISOString() };
};

export const timeout = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export function formatNumber(value: number | string, options?: Intl.NumberFormatOptions) {
  if (!value && value !== 0) return '-';
  let styleObj: Intl.NumberFormatOptions = {
    style: 'currency',
    currencyDisplay: 'symbol',
    currency: 'NGN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  };
  if (options) styleObj = { ...styleObj, ...options };

  return new Intl.NumberFormat('en-NG', styleObj).format(+value);
}

export const getLiveTripOrderStatus = (order: LiveTripOrderType): ENUM_STATUS | 'completed' => {
  const {
    kitchen_prepared,
    kitchen_dispatched,
    calculated_order,
    kitchen_accepted,
    shop_accepted,
    shop_prepared,
    completed
  } = order;
  if (completed) return 'completed';
  if (!kitchen_accepted) {
    return ENUM_STATUS.PENDING;
  } else if (
    (kitchen_accepted || shop_accepted) &&
    (!kitchen_prepared || !shop_prepared) &&
    !kitchen_dispatched
  ) {
    return ENUM_STATUS.PROCESSING;
  } else if (
    kitchen_prepared &&
    shop_prepared &&
    !kitchen_dispatched &&
    !calculated_order?.pickup
  ) {
    return ENUM_STATUS.READY;
  } else if (!calculated_order?.pickup && kitchen_dispatched) {
    return ENUM_STATUS.ONGOING;
  }
  return ENUM_STATUS.PENDING;
};

export const numberWithCommas = (x: string | number): string => {
  return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const copyTextToClipboard = async (text: string) => {
  return await navigator.clipboard.writeText(text);
};

export const getCokitchenList = async () => {
  let data = [];
  const dataString = localStorage.getItem(STORAGE_KEYS.COKITCHEN_LIST);
  if (dataString) {
    data = JSON.parse(dataString);
  } else {
    data = await initKitchenData();
  }
  return data;
};

export const getLocationOptions = (item: CoKitchenType[]) => {
  const options = [];
  for (let i = 0; i < item?.length; i++) {
    if (item)
      options.push({
        value: item[i]?.id,
        text: item[i]?.name
      });
  }
  return options;
};
export const getRealTime = () => {
  const interval = setInterval(() => {
    const time = moment();
    return time;
  }, 1000);

  return () => clearInterval(interval);
};
export const getKitchenTeam = () => {
  const timestamp = new Date();
  const convertedTimestamp = moment(timestamp).utcOffset('+01:00');

  const time = convertedTimestamp.hours();

  if (time >= 6 && time < 14) {
    return ENUM_KITHEN_TEAMS.ALPHA;
  }

  if (time >= 14 && time < 22) {
    return ENUM_KITHEN_TEAMS.BETA;
  }

  if (time >= 22 || time < 6) {
    return ENUM_KITHEN_TEAMS.CHAI;
  }
};

export const getEmoji = (name: string) => {
  const nameLower = name.toLowerCase();
  let emoji;
  if (nameLower.includes('extra')) {
    emoji = '🍕';
  } else if (
    nameLower.includes('protein') ||
    nameLower.includes('chicken') ||
    nameLower.includes('wing')
  ) {
    emoji = '🍗';
  } else if (nameLower.includes('side')) {
    emoji = '🍎';
  } else if (nameLower.includes('egg')) {
    emoji = '🥚';
  } else if (nameLower.includes('drink')) {
    emoji = '🥤';
  } else if (nameLower.includes('sauce') || nameLower.includes('spicy')) {
    emoji = '🌶️';
  } else if (nameLower.includes('drizzle')) {
    emoji = '🤤';
  } else if (
    nameLower.includes('swallow') ||
    nameLower.includes('number') ||
    nameLower.includes('favourite') ||
    nameLower.includes('quantity')
  ) {
    emoji = '😋';
  } else if (nameLower.includes('bacon')) {
    emoji = '🥓';
  } else if (nameLower.includes('cheese')) {
    emoji = '🧀';
  } else if (nameLower.includes('sausage')) {
    emoji = '🌭';
  }

  return emoji;
};

export function timeConvert(n: number) {
  const num = n;
  const hours = num / 3600;
  const rhours = Math.floor(hours);
  const minutes = (num % 3600) / 60;
  const rminutes = Math.round(minutes);

  let dateString;

  if (rhours === 0 && rminutes !== 0) {
    dateString = rminutes + ' mins';
  } else if (rminutes === 0 && rhours !== 0) {
    dateString = rhours + ' hr';
  } else if (rhours === 1 && rminutes !== 0) {
    dateString = rhours + ' hr ' + rminutes + ' mins';
  } else if (rhours === 1 && rminutes === 0) {
    dateString = rhours + ' hr ';
  } else if (rhours !== 0 && rminutes === 1) {
    dateString = rhours + ' hr ' + rminutes + ' min';
  } else if (rhours === 0 && rminutes === 1) {
    dateString = rminutes + ' min';
  } else {
    dateString = rhours + ' hrs ' + rminutes + ' mins';
  }

  return dateString;
}

export const getInterval = (activeDateRangeSelector: ENUM_DATE_RANGE) => {
  let interval;
  if (activeDateRangeSelector === ENUM_DATE_RANGE.TODAY) {
    interval = 'TODAY';
  } else if (activeDateRangeSelector === ENUM_DATE_RANGE.THIS_WEEK) {
    interval = 'THIS_WEEK';
  } else if (activeDateRangeSelector === ENUM_DATE_RANGE.THIS_MONTH) {
    interval = 'THIS_MONTH';
  } else if (activeDateRangeSelector === ENUM_DATE_RANGE.LAST_MONTH) {
    interval = 'LAST_MONTH';
  } else if (activeDateRangeSelector === ENUM_DATE_RANGE.YESTERDAY) {
    interval = 'YESTERDAY';
  }
  return interval;
};

export const getDurationSince = (timeObj: Date, timeSince: Date) => {
  const diffInSecs = (moment(timeSince).valueOf() - moment(timeObj).valueOf()) / 1000;
  if (diffInSecs >= 24 * 60 * 60) {
    const diff = `${diffInSecs / (24 * 60 * 60)}`;
    return `${diff.split('.')[0]} days`;
  }
  if (diffInSecs >= 60 * 60) {
    const diff = diffInSecs / (60 * 60);
    const hours = Math.floor(diff);
    const mins = `${diff}`.split('.')[1].substring(0, 2);
    return `${hours} hr ${Math.floor((+mins * 60) / 100)} min`;
  }
  if (diffInSecs >= 60 || diffInSecs < 0) {
    const diff = diffInSecs / 60;
    const mins = Math.abs(Math.floor(diff));
    const secs = `${diff}`.split('.')[1].substring(0, 2);
    return `${mins} min ${Math.floor((+secs * 60) / 100)} sec`;
  }
  const diff = `${Math.abs(diffInSecs)}`;
  return `${diff.split('.')[0]} sec`;
};

export const getBankLogo = (bankName: ENUM_BANK_NAMES) => {
  let logo_url;
  if (bankName === ENUM_BANK_NAMES['9PSB']) {
    logo_url =
      'https://d3u7b9fq2opvwp.cloudfront.net/upload-service/1685545971591f6f886df-ad3e-4237-9175-de3bea9dbe96';
  } else if (bankName === ENUM_BANK_NAMES.ACCESS) {
    logo_url =
      'https://d3u7b9fq2opvwp.cloudfront.net/upload-service/16855462250115d4895d1-eda3-4838-bbb1-9c6a6765e51a';
  } else if (bankName === ENUM_BANK_NAMES.FCMB) {
    logo_url =
      'https://d3u7b9fq2opvwp.cloudfront.net/upload-service/1685546344704e6def974-3104-4d2a-b939-d2348920218b';
  } else if (bankName === ENUM_BANK_NAMES.GT) {
    logo_url =
      'https://d3u7b9fq2opvwp.cloudfront.net/upload-service/168554625311341b615fc-d7f4-436c-8b1e-2692ac2c4b65';
  } else if (bankName === ENUM_BANK_NAMES.HERITAGE) {
    logo_url =
      'https://d3u7b9fq2opvwp.cloudfront.net/upload-service/1685546365023459912c8-0de4-4c9f-8f19-0c17bf180115';
  } else if (bankName === ENUM_BANK_NAMES.PROVIDUS) {
    logo_url =
      'https://d3u7b9fq2opvwp.cloudfront.net/upload-service/168554618629907c1d4b1-36af-4bac-b3d6-ecf7424bb00a';
  } else if (bankName === ENUM_BANK_NAMES.STANBIC) {
    logo_url =
      'https://d3u7b9fq2opvwp.cloudfront.net/upload-service/1685546310976e3583334-c01c-44f2-825b-5fdc97b96428';
  } else {
    logo_url =
      'https://d3u7b9fq2opvwp.cloudfront.net/upload-service/16855462843845e654de2-5f08-4b4e-b107-15d169c4b796';
  }
  return logo_url;
};

export const FormatNumberOptions: Record<
  'currency' | 'decimal' | 'percent' | 'currencyWithNoDp' | 'decimalWithNoDp' | 'percentWithNoDp',
  Intl.NumberFormatOptions
> = {
  currency: {
    style: 'currency',
    currencyDisplay: 'symbol',
    currency: 'NGN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  },
  decimal: {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    style: 'decimal'
  },
  percent: {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    style: 'percent'
  },
  currencyWithNoDp: {
    style: 'currency',
    currencyDisplay: 'symbol',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  },
  decimalWithNoDp: {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    style: 'decimal'
  },
  percentWithNoDp: {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    style: 'percent'
  }
};

export const noBaniAccountNo = (user: UserType) => {
  return (
    !user?.bani_account_number &&
    !user?.bani_account_number_fcmb &&
    !user?.bani_account_number_9ps &&
    !user?.bani_account_number_gt &&
    !user?.bani_account_number_providus &&
    !user?.bani_account_number_stanbic &&
    !user?.bani_account_number_heritage
  );
};

export const getOpeningTime = (workHours: Array<{ day: string; opening_time: string }>): string => {
  const dayAfterToday = moment().add(1, 'days').format('YYYY-MM-DD');
  const tomorrowDay = moment(dayAfterToday).format('dddd');
  // get opening time for tomorrow
  const tomorrowDayWorkingHours = workHours?.filter((itm: { day: string }) => {
    return itm?.day?.includes(tomorrowDay.toLocaleLowerCase());
  });
  const opening_time = tomorrowDayWorkingHours[0]?.opening_time;
  const tomorrow = moment().add(1, 'days').format('YYYY-MM-DD');
  const tomorrowDate = moment(tomorrow).format();
  // get the hour before openingTime
  const openingTime = moment(opening_time, 'HH:mm:ss').subtract(1, 'hours').format('HH:mm:ss');
  const openingTimeTomorrow = tomorrowDate.replace('00:00:00', openingTime);
  return openingTimeTomorrow;
};

export const isCSRole = () => {
  return getCurrentTeam() === ENUM_COKITCHEN_TEAM.CS;
};

export const getCSAdmin = () => {
  const role = localStorage.getItem(STORAGE_KEYS.ROLE) as ENUM_ROLE;
  return [ENUM_ROLE.CS_ADMIN, ENUM_ROLE.SUPER_ADMIN, ENUM_ROLE.CS_SUPER_ADMIN].includes(role);
};

export const isCSRep = () => {
  const role = localStorage.getItem(STORAGE_KEYS.ROLE) as ENUM_ROLE;
  return [ENUM_ROLE.CS, ENUM_ROLE.CS_INBOUND, ENUM_ROLE.CS_OUTBOUND].includes(role);
};

export const calcDeliveryTime = (
  deliveryTime: string | null | undefined,
  dispatchTime: string | null | undefined
) => {
  if (deliveryTime && dispatchTime) {
    const kitchenDispatchedTime = moment(dispatchTime);
    const estimatedTime = kitchenDispatchedTime.add(deliveryTime, 'minutes').format();

    const now = moment();
    const then = moment(estimatedTime);
    const countdown = moment(then.diff(now));
    const minutes = countdown.minutes();
    return minutes;
  }
};

export const normalizeRating = (results: { rate: string; total_review: string }[]) => {
  const cloned = [...results];

  ['1.00', '2.00', '3.00', '4.00', '5.00'].forEach((score) => {
    const found = cloned.some(({ rate }) => rate === score);
    if (!found) cloned.push({ rate: score, total_review: '0' });
  });

  cloned.sort((a, b) => {
    return Number(a.rate) > Number(b.rate) ? -1 : 1;
  });

  return cloned;
};

export const toSnakeCase = (input: string): string => {
  return input.trim().replace(/\s+/g, '_').toLowerCase();
};

export const timeFormatter = (timeString: string) => {
  if (!timeString) return;
  const [hourString, minute] = timeString.split(':');
  const hour = +hourString % 24;
  return (hour % 12 || 12) + ':' + minute + (hour < 12 ? 'AM' : 'PM');
};

export const handleApiError = (error: unknown) => {
  const message = (error as any)?.response?.data?.message;
  const messages = Array.isArray(message) ? message : [message];
  messages.forEach((message) => {
    if (message) toast(<ToastMessage type='error' message={message} />);
  });
};

export const isGlovoOrder = (order: LiveTripOrderType | null | OrderType) => {
  return order?.delivery === ENUM_ORDER_PROVIDER.glovo;
};
export const isChowdeckOrder = (order: LiveTripOrderType | null | OrderType) => {
  return order?.delivery === ENUM_ORDER_PROVIDER.chowdeck;
};

export const replaceNigeriaCountryCode = (phoneNumber: string): string => {
  if (phoneNumber.startsWith('+234')) phoneNumber = phoneNumber.replace('+234', '0');
  return phoneNumber;
};

export function pipe<T>(fns: ((value: T) => T)[]) {
  return (acc: T) => fns.reduce((v, f) => f(v), acc);
}
